import { ModalFieldConfig, ModalProps } from '../modal-field.interface';
import { FormGroup } from '@angular/forms';
import { convertHexToDecimal } from 'src/utils/other.util';

const HEX_FIELD_NAMES = new Set(['address', 'parameterGroupNumber']);

export function markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        control?.markAsTouched();
        if (control instanceof FormGroup) {
            markFormGroupTouched(control);
        }
    });
}

export function getLatestVersion(anchor: unknown): Record<string, unknown> | undefined {
    const anchorData = anchor as {
        validVersion?: Record<string, unknown>;
        allVersions?: Record<string, unknown>[];
    };
    const versions = anchorData?.allVersions?.filter(Boolean) ?? [];

    if (anchorData?.validVersion) {
        return anchorData.validVersion;
    }

    if (versions.length > 0) {
        const activeVersions = versions.filter(
            (version) => !version['deletedTimestamp'] && !version['deletedInWorkspaceKey']
        );
        const candidates = activeVersions.length > 0 ? activeVersions : versions;

        return [...candidates].sort((a, b) => getVersionSortValue(b) - getVersionSortValue(a))[0];
    }

    return anchorData?.validVersion;
}

function getVersionSortValue(version: Record<string, unknown>): number {
    const key = Number(version['key']);
    if (!Number.isNaN(key) && key > 0) return key;

    const createdTimestamp = version['createdTimestamp'];
    const committedTimestamp = version['committedTimestamp'];
    const timestamp = createdTimestamp ?? committedTimestamp;

    if (typeof timestamp === 'string' || timestamp instanceof Date) {
        const time = new Date(timestamp).getTime();
        return Number.isNaN(time) ? 0 : time;
    }

    return 0;
}

export function getFieldValue(
    field: ModalFieldConfig,
    modalProps: ModalProps,
    latestVersion?: Record<string, unknown>
): unknown {
    const isAnchorField = (modalProps.anchorFields || []).some((f) => f.field === field.field);

    if (isAnchorField && modalProps.anchor) {
        const value = (modalProps.anchor as Record<string, unknown>)?.[field.field] ?? '';
        return formatHexFieldValue(field.field, value);
    }

    const value =
        (modalProps.version as Record<string, unknown>)?.[field.field] ??
        latestVersion?.[field.field] ??
        '';
    return formatHexFieldValue(field.field, value);
}

export function applyDefaultValue(field: ModalFieldConfig, value: unknown): unknown {
    if (
        (field.type === 'number' ||
            field.type === 'switch' ||
            field.type === 'dropdown' ||
            field.type === 'lazy-dropdown' ||
            field.type === 'text') &&
        (value === '' || value === null || value === undefined) &&
        field.defaultValue !== undefined
    ) {
        return field.defaultValue;
    }
    return value;
}

export function processFormData(
    formValue: Record<string, unknown>,
    anchorFieldNames: string[],
    versionFieldNames: string[]
): { anchorData: Record<string, unknown>; versionData: Record<string, unknown> } {
    const anchorData: Record<string, unknown> = { logText: '' };
    const versionData: Record<string, unknown> = { logText: '', name: '', comment: '' };

    Object.keys(formValue).forEach((key) => {
        if (anchorFieldNames.includes(key)) {
            anchorData[key] = parseHexFieldValue(key, formValue[key]);
        } else if (versionFieldNames.includes(key)) {
            versionData[key] = parseHexFieldValue(key, formValue[key]);
        }
    });

    if (versionData['generation'] && typeof versionData['generation'] === 'string') {
        versionData['generation'] = parseInt(versionData['generation'] as string, 10);
    }

    if (versionData['isActive'] !== undefined) {
        versionData['isActive'] = Boolean(versionData['isActive']);
    }

    return { anchorData, versionData };
}

const HEX_FIELD_PAD_LENGTH: Record<string, number> = {
    address: 2,
    parameterGroupNumber: 5,
};

export function formatHexFieldValue(fieldName: string, value: unknown): unknown {
    if (!HEX_FIELD_NAMES.has(fieldName) || typeof value !== 'number') return value;
    const padLength = HEX_FIELD_PAD_LENGTH[fieldName] ?? 4;
    return '0x' + value.toString(16).toUpperCase().padStart(padLength, '0');
}

export function parseHexFieldValue(fieldName: string, value: unknown): unknown {
    if (!HEX_FIELD_NAMES.has(fieldName) || typeof value !== 'string') {
        return value;
    }

    const normalizedValue = value.trim();
    if (!normalizedValue) {
        return value;
    }

    return convertHexToDecimal(normalizedValue);
}

export function createServiceConfig(component: Record<string, unknown>) {
    return {
        addresses: {
            service: component['addressService'],
            prop: 'addresses',
            loading: 'addressesIsLoading',
            limit: component['addressesLimit'],
        },
        componentCodes: {
            service: component['componentCodeService'],
            prop: 'componentCodes',
            loading: 'componentCodesIsLoading',
            limit: component['componentCodesLimit'],
        },
        components: {
            service: component['componentService'],
            prop: 'components',
            loading: 'componentsIsLoading',
            limit: component['componentsLimit'],
        },
        functionCategories: {
            service: component['functionCategoryService'],
            prop: 'functionCategories',
            loading: 'functionCategoriesIsLoading',
            limit: component['functionCategoriesLimit'],
        },
        useCases: {
            service: component['useCaseService'],
            prop: 'useCases',
            loading: 'useCasesIsLoading',
            limit: component['useCasesLimit'],
        },
        segments: {
            service: component['segmentService'],
            prop: 'segments',
            loading: 'segmentsIsLoading',
            limit: component['segmentsLimit'],
        },
        allocationElements: {
            service: component['allocationElementService'],
            prop: 'allocationElements',
            loading: 'allocationElementsIsLoading',
            limit: component['allocationElementsLimit'],
        },
        fvBoxPortDataRelations: {
            service: component['fvBoxPortDataRelationService'],
            prop: 'fvBoxPortDataRelations',
            loading: 'fvBoxPortDataRelationsIsLoading',
            limit: component['fvBoxPortDataRelationsLimit'],
        },
        signals: {
            service: component['signalService'],
            prop: 'signals',
            loading: 'signalsIsLoading',
            limit: component['signalsLimit'],
        },
        valueDescriptions: {
            service: component['valueDescriptionService'],
            prop: 'valueDescriptions',
            loading: 'valueDescriptionsIsLoading',
            limit: component['valueDescriptionsLimit'],
        },
        userFunctions: {
            service: component['userFunctionService'],
            prop: 'userFunctions',
            loading: 'userFunctionsIsLoading',
            limit: component['userFunctionsLimit'],
        },
    };
}
